import {
  Component,
  DestroyRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActionComponent,
  ActionOnComponent,
  ConfigSlideIn,
  SlidingFormProgressStyle,
  Widget,
  WidgetComponent,
  WidgetComponentSubType,
} from 'app/campaign/widgets.model';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { WidgetService } from 'app/shared/services/widget.service';
import { FlyoutService } from '../../flyout.service';

type AddButtonType = 'submit-btn' | 'back-btn';

@Component({
  selector: 'app-flyout-form-settings-form-options',
  templateUrl: './flyout-form-settings-form-options.component.html',
  styleUrl: './flyout-form-settings-form-options.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('expandReveal', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class FlyoutFormSettingsFormOptionsComponent implements OnInit, OnChanges {
  @Input() public widget: Widget | null = null;

  private readonly widgetService = inject(WidgetService);
  private readonly widgetEditorDataService = inject(WidgetEditorDataService);
  private readonly flyoutService = inject(FlyoutService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly destroyRef = inject(DestroyRef);

  public readonly slidingFormProgressStyles: ReadonlyArray<{ key: SlidingFormProgressStyle; value: string }> = [
    { key: SlidingFormProgressStyle.None, value: 'generic.none' },
    {
      key: SlidingFormProgressStyle.Dots,
      value: 'campaign.widget.sidebar.form-settings.sliding-form-styles-select-dots',
    },
  ];

  public isPartialStepsActivated = false;
  public isSplitInputsToStepsActivated = false;
  public isSplitInputsToStepsPossible = false;
  public isSplitInputsToStepsPossibleError: string | undefined;
  public backButtonComponents: WidgetComponent[] = [];
  public slideInFormIsExpanded = false;
  public savingPartialSteps = false;
  public savingSplitInputsToSteps = false;
  public configSlideIn: ConfigSlideIn = {
    selectedSlidingFormProgressStyle: SlidingFormProgressStyle.None,
    selectedSlidingFormLabelName: '',
  };

  public get isPropertySlideInDisabled(): boolean {
    return !this.isSplitInputsToStepsPossible || !this.isSplitInputsToStepsActivated;
  }

  public ngOnInit(): void {
    // The buttons stepping requires are created by the editor's left sidebar on the editor's own widget
    // copy, not on this input, so the reference never changes and ngOnChanges cannot see them arrive.
    this.widgetEditorDataService.actionOnComponentBS.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => {
      if (this.isComponentCountChange(action)) {
        this.applySplitPossibility(this.widgetEditorDataService.widget);
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['widget'] && this.widget) {
      this.initFromWidget();
    }
  }

  private initFromWidget(): void {
    if (!this.widget) {
      return;
    }
    this.isPartialStepsActivated = this.widget.usePartialFormSubmit === true;
    this.isSplitInputsToStepsActivated = this.widget.splitInputsToSteps === true;

    this.applySplitPossibility(this.widget);

    this.configSlideIn = {
      selectedSlidingFormProgressStyle: this.widget.slidingFormConfig?.progressStyle ?? SlidingFormProgressStyle.None,
      selectedSlidingFormLabelName: this.widget.slidingFormConfig?.nextButtonText ?? '',
    };
  }

  private isComponentCountChange(action: ActionComponent | undefined): boolean {
    return action?.action === ActionOnComponent.Add || action?.action === ActionOnComponent.Remove;
  }

  private applySplitPossibility(widget: Widget | null | undefined): void {
    if (!widget || widget.id !== this.widget?.id) {
      return;
    }
    const [possible, error, backButtons] = WidgetComponentHelpers.checkIfPossibleToSplitInputsToSteps(widget) as [
      boolean,
      string | undefined,
      WidgetComponent[],
    ];
    this.isSplitInputsToStepsPossible = possible;
    this.isSplitInputsToStepsPossibleError = error;
    this.backButtonComponents = backButtons ?? [];
    this.isSplitInputsToStepsActivated = possible && widget.splitInputsToSteps === true;
  }

  public togglePartialSteps(): void {
    if (!this.widget) {
      return;
    }
    const value = this.isPartialStepsActivated;
    this.widget.usePartialFormSubmit = value;
    this.savingPartialSteps = true;
    this.widgetService
      .patchWidget(this.widget.id, '/usePartialFormSubmit', value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.syncEditorWidget((widget) => (widget.usePartialFormSubmit = value));
          this.savingPartialSteps = false;
          this.markUnpublished();
        },
        error: () => {
          this.savingPartialSteps = false;
          this.snackBarService.error('generic.errorOccurred');
          this.isPartialStepsActivated = this.widgetEditorDataService.widget?.usePartialFormSubmit === true;
        },
      });
  }

  public toggleSplitInputsToSteps(): void {
    if (!this.widget) {
      return;
    }
    const value = this.isSplitInputsToStepsActivated;
    this.widget.splitInputsToSteps = value;
    // Stepping defaults the progress indicator to dots.
    this.widget.slidingFormConfig = { ...this.widget.slidingFormConfig, progressStyle: SlidingFormProgressStyle.Dots };
    this.savingSplitInputsToSteps = true;
    this.widgetService
      .updateWidget(this.widget.id, this.widget)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.syncEditorWidget((widget) => {
            widget.splitInputsToSteps = value;
            widget.slidingFormConfig = { ...widget.slidingFormConfig, progressStyle: SlidingFormProgressStyle.Dots };
            this.refreshSubmitButton(widget);
          });
          this.configSlideIn = {
            selectedSlidingFormProgressStyle:
              this.widget?.slidingFormConfig?.progressStyle ?? SlidingFormProgressStyle.None,
            selectedSlidingFormLabelName: this.widget?.slidingFormConfig?.nextButtonText ?? '',
          };
          this.savingSplitInputsToSteps = false;
          this.markUnpublished();
        },
        error: () => {
          this.savingSplitInputsToSteps = false;
          this.snackBarService.error('generic.errorOccurred');
          this.isSplitInputsToStepsActivated = !value;
        },
      });
  }

  public updateSlidingFormProgressStyle(): void {
    if (!this.widget) {
      return;
    }
    const style = this.configSlideIn.selectedSlidingFormProgressStyle;
    this.widget.slidingFormConfig = {
      progressStyle: style === SlidingFormProgressStyle.None ? undefined : style,
      nextButtonText: this.configSlideIn.selectedSlidingFormLabelName || undefined,
    };
    this.savingSplitInputsToSteps = true;
    this.widgetService
      .updateWidget(this.widget.id, this.widget)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.syncEditorWidget((widget) => (widget.slidingFormConfig = { ...this.widget?.slidingFormConfig }));
          this.savingSplitInputsToSteps = false;
          this.markUnpublished();
        },
        error: () => {
          this.savingSplitInputsToSteps = false;
          this.snackBarService.error('generic.errorOccurred');
        },
      });
  }

  public toggleExpandSlideInForm(event: Event): void {
    const target = event.target as HTMLElement | null;
    // Let clicks on the toggle itself flip the setting rather than expand/collapse the panel.
    if (!target?.closest('mat-slide-toggle')) {
      event.preventDefault();
      this.slideInFormIsExpanded = !this.slideInFormIsExpanded;
    }
  }

  public addButton(type: AddButtonType): void {
    this.flyoutService.flyoutFormAddCompBS.next(type);
  }

  private refreshSubmitButton(widget: Widget): void {
    const submitButton = WidgetComponentHelpers.getAllComponentsOnWidget(widget).find(
      (component) => component.subType === WidgetComponentSubType.ButtonSubmit,
    );
    if (submitButton) {
      this.widgetEditorDataService.forceRefreshOnComponent(submitButton);
      // The canvas re-renders the submit button off this stream; without it its label stays stale.
      this.widgetEditorDataService.refreshComponentSubject.next(submitButton);
    }
  }

  private syncEditorWidget(mutate: (widget: Widget) => void): void {
    const editorWidget = this.widgetEditorDataService.widget;
    if (editorWidget) {
      mutate(editorWidget);
    }
  }

  private markUnpublished(): void {
    this.widgetService.hasUnpublishedChangesBS.next(true);
  }
}
